"use client";

import { DummyRichText, Theme } from "@/types/data";
import styles from "./QuoteBlock.module.scss";
import { useState } from "react";
import clsx from "clsx";
import { useMotionTemplate, useSpring, useTransform } from "framer-motion";
import { motion } from "framer-motion";
import { Button } from "@/components/shared/Button/Button";
import IconArrowLongLeft from "@/images/icons/arrow-long-left.svg";
import { HighlightText, SiteThemes } from "@/sanity/types";
import { PortableTextRichText } from "../../shared/PortableTextRichText/PortableTextRichText";
import { kebabCase } from "@/utilities/string-helpers";

interface QuoteBlockProps {
	customId?: string | null;
	heading?: HighlightText | null;
	quoteItems?: QuoteItemProps[] | null;
	theme?: SiteThemes | null;
}

export const QuoteBlock = ({ customId, heading, quoteItems, theme }: QuoteBlockProps) => {
	const [activeSlide, setActiveSlide] = useState(0);

	const xInterpolated = useSpring(0, {
		stiffness: 250,
		damping: 50,
		mass: 3,
	});

	const xPercent = useTransform(xInterpolated, [0, 1], [0, -100], {
		clamp: false,
	});

	const transform = useMotionTemplate`translateX(${xPercent}%)`;

	if (!quoteItems) {
		return null;
	}

	const enabledPrev = activeSlide > 0;
	const enabledNext = activeSlide < quoteItems.length - 1;

	function goTo(index: number) {
		setActiveSlide(index);
		xInterpolated.set(index);
	}

	return (
		<section
			id={customId ? kebabCase(customId) : undefined}
			className={clsx(styles["quote-block"], styles[`quote-block--${theme}`])}
			data-theme={theme}
		>
			{heading && (
				<h2 className={clsx(styles["quote-block__heading"])}>
					<PortableTextRichText
						asFragment
						value={heading}
					/>
				</h2>
			)}
			{quoteItems.length > 0 && (
				<Button
					label={<IconArrowLongLeft />}
					aria-label="Previous Slide"
					className={clsx(styles["quote-block__button"], styles["quote-block__button--prev"])}
					innerClassName={styles["quote-block__button-inner"]}
					disabled={!enabledPrev}
					onClick={() => goTo(activeSlide + -1)}
				/>
			)}
			<div className={styles["quote-block__inner"]}>
				<div className={styles["quote-block__track-wrapper"]}>
					<motion.div
						style={{ transform }}
						className={styles["quote-block__track"]}
					>
						{quoteItems.map((quoteItem, index) => (
							<QuoteItem
								aria-label={`Slide ${index + 1}`}
								aria-hidden={activeSlide !== index ? "true" : "false"}
								key={index}
								body={quoteItem.body}
								caption={quoteItem.caption}
								className={clsx(activeSlide === index && styles["quote-item--active"])}
							/>
						))}
					</motion.div>
				</div>
			</div>
			{quoteItems.length > 0 && (
				<Button
					label={<IconArrowLongLeft />}
					aria-label="Next Slide"
					className={clsx(styles["quote-block__button"], styles["quote-block__button--next"])}
					innerClassName={styles["quote-block__button-inner"]}
					disabled={!enabledNext}
					onClick={() => goTo(activeSlide + 1)}
				/>
			)}
			<ul className={styles["quote-block__dots"]}>
				{quoteItems.map((_quoteItem, index) => (
					<li key={index}>
						<button
							aria-label={`Go to slide ${index + 1}`}
							className={clsx(
								styles["quote-block__dot"],
								activeSlide === index && styles["quote-block__dot--active"],
							)}
							onClick={() => goTo(index)}
						></button>
					</li>
				))}
			</ul>
		</section>
	);
};

interface QuoteItemProps extends React.ComponentPropsWithoutRef<"figure"> {
	body?: string;
	caption?: string;
	className?: string;
}
export const QuoteItem = ({ body, caption, className, ...props }: QuoteItemProps) => {
	return (
		<figure
			className={clsx(styles["quote-item"], className)}
			{...props}
		>
			{body && <blockquote className={styles["quote-item__body"]}>{body}</blockquote>}
			{caption && <figcaption className={styles["quote-item__caption"]}>{caption}</figcaption>}
		</figure>
	);
};
